import React from 'react';
import { TrendingUp, Trophy, Target, Clock, Users, BarChart3 } from 'lucide-react';

const GameStats = ({ 
  stats = {}, 
  gameInfo = {},
  className = ""
}) => {
  const {
    totalGames = 0,
    wins = 0,
    losses = 0,
    ties = 0,
    totalPoints = 0,
    avgGameDuration = 0,
    winRate = 0,
    uniquePlayers = 0
  } = stats;

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatPercentage = (value) => {
    return `${Math.round(value * 100)}%`;
  };

  const getWinRateColor = (rate) => {
    if (rate >= 0.7) return 'text-green-400';
    if (rate >= 0.5) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Game Header */}
      {gameInfo && (
        <div className="card">
          <div className="flex items-center space-x-4">
            <div className="text-4xl">{gameInfo.emoji}</div>
            <div>
              <h3 className="text-xl font-bold text-discord-lightest">
                {gameInfo.name} Statistics
              </h3>
              <p className="text-discord-light">{gameInfo.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Games */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-discord-light">Total Games</p>
              <p className="text-2xl font-bold text-discord-lightest">{totalGames}</p>
            </div>
            <BarChart3 className="h-8 w-8 text-blue-400" />
          </div>
        </div>

        {/* Win Rate */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-discord-light">Win Rate</p>
              <p className={`text-2xl font-bold ${getWinRateColor(winRate)}`}>
                {formatPercentage(winRate)}
              </p>
            </div>
            <Target className="h-8 w-8 text-green-400" />
          </div>
        </div>

        {/* Total Points */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-discord-light">Total Points</p>
              <p className="text-2xl font-bold text-discord-lightest">{totalPoints}</p>
            </div>
            <Trophy className="h-8 w-8 text-yellow-400" />
          </div>
        </div>

        {/* Avg Duration */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-discord-light">Avg Duration</p>
              <p className="text-2xl font-bold text-discord-lightest">
                {formatDuration(avgGameDuration)}
              </p>
            </div>
            <Clock className="h-8 w-8 text-purple-400" />
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Game Results Breakdown */}
        <div className="card">
          <h4 className="text-lg font-bold text-discord-lightest mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-green-400" />
            Game Results
          </h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-discord-light">Wins</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-discord-darker rounded-full h-2">
                  <div 
                    className="bg-green-400 h-2 rounded-full" 
                    style={{ width: `${totalGames > 0 ? (wins / totalGames) * 100 : 0}%` }}
                  ></div>
                </div>
                <span className="text-discord-lightest font-medium">{wins}</span>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-discord-light">Losses</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-discord-darker rounded-full h-2">
                  <div 
                    className="bg-red-400 h-2 rounded-full" 
                    style={{ width: `${totalGames > 0 ? (losses / totalGames) * 100 : 0}%` }}
                  ></div>
                </div>
                <span className="text-discord-lightest font-medium">{losses}</span>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-discord-light">Ties</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-discord-darker rounded-full h-2">
                  <div 
                    className="bg-yellow-400 h-2 rounded-full" 
                    style={{ width: `${totalGames > 0 ? (ties / totalGames) * 100 : 0}%` }}
                  ></div>
                </div>
                <span className="text-discord-lightest font-medium">{ties}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="card">
          <h4 className="text-lg font-bold text-discord-lightest mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2 text-blue-400" />
            Additional Info
          </h4>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-discord-light">Unique Players</span>
              <span className="text-discord-lightest font-medium">{uniquePlayers}</span>
            </div>
            
            {gameInfo && (
              <>
                <div className="flex justify-between">
                  <span className="text-discord-light">Category</span>
                  <span className="text-discord-lightest font-medium capitalize">
                    {gameInfo.category}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-discord-light">Subscription Tier</span>
                  <span className="text-discord-lightest font-medium capitalize">
                    {gameInfo.subscriptionTier}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Performance Indicators */}
      <div className="card">
        <h4 className="text-lg font-bold text-discord-lightest mb-4">
          Performance Indicators
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-discord-lightest">
              {totalGames > 0 ? Math.round(totalPoints / totalGames) : 0}
            </div>
            <p className="text-sm text-discord-light">Avg Points per Game</p>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-discord-lightest">
              {totalGames > 0 ? Math.round(avgGameDuration / 60) : 0}m
            </div>
            <p className="text-sm text-discord-light">Avg Duration</p>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-discord-lightest">
              {uniquePlayers > 0 ? Math.round(totalGames / uniquePlayers * 10) / 10 : 0}
            </div>
            <p className="text-sm text-discord-light">Games per Player</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameStats;
